import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  CHANGE_WEBHOOK_ACTIVITY,
  ChangeWebhookActivityAction,
  ChangeWebhookActivitySuccessAction,
  DELETE_WEBHOOK,
  DELETE_WEBHOOK_SUCCESS,
  DeleteWebhookAction,
  DeleteWebhookSuccessAction,
  GET_ALL_WEBHOOKS,
  GET_ALL_WEBHOOKS_SUCCESS,
  GET_MSISDNS_FOR_WEBHOOKS,
  GET_SINGLE_WEBHOOK,
  GET_WEBHOOK_TYPES,
  GetAllWebhooksSuccessAction,
  GetMsisdnsForWebhooksSuccessAction,
  GetSingleWebhookAction,
  GetSingleWebhookSuccessAction,
  GetWebhookTypesSuccessAction,
  SAVE_WEBHOOK,
  SAVE_WEBHOOK_SUCCESS,
  SaveWebhookAction,
  SaveWebhookSuccessAction,
  UPDATE_WEBHOOK,
  UPDATE_WEBHOOK_SUCCESS,
  UpdateWebhookAction,
  UpdateWebhookSuccessAction
} from 'app/webhooks/webhooks.actions';
import { WebhooksService } from 'app/webhooks/webhooks.service';
import { MsisdnService } from 'app/shared/msisdn/msisdn.service';
import { WebhookListItem, WebhookSubscription } from 'app/webhooks/model/webhooks.model';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IState } from 'app/shared/models/state';
import { getWebhookListSelector } from 'app/webhooks/webhooks.reducer';
import { Ng1ModalService } from '../shared/ng1/ng1-modal.service';
import { EMPTY } from 'rxjs';

@Injectable()
export class WebhooksEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private webhooksService: WebhooksService,
    private msisdnService: MsisdnService,
    private store: Store<IState>,
    private ng1ModalService: Ng1ModalService
  ) {}

  @Effect()
  getAllWebhooks = this.actions$.pipe(
    ofType(GET_ALL_WEBHOOKS),
    switchMap(() => this.webhooksService.getAll().pipe(map(response => new GetAllWebhooksSuccessAction(response))))
  );

  @Effect({ dispatch: false })
  redirectToCreatorIfNoWebhooks = this.actions$.pipe(
    ofType(GET_ALL_WEBHOOKS_SUCCESS, DELETE_WEBHOOK_SUCCESS),
    withLatestFrom(this.store.select(getWebhookListSelector)),
    tap(([_, webhooks]: [void, WebhookListItem[]]) => {
      if (!webhooks.length) {
        this.router.navigateByUrl('/webhooks/new');
      }
    })
  );

  @Effect()
  getMsisdnsForWebhooks = this.actions$.pipe(
    ofType(GET_MSISDNS_FOR_WEBHOOKS),
    switchMap(() =>
      this.msisdnService.inboundableMsisdns().pipe(map(response => new GetMsisdnsForWebhooksSuccessAction(response)))
    )
  );

  @Effect()
  getWebhookTypes = this.actions$.pipe(
    ofType(GET_WEBHOOK_TYPES),
    switchMap(() => this.webhooksService.getTypes().pipe(map(response => new GetWebhookTypesSuccessAction(response))))
  );

  @Effect()
  getSingleWebhook = this.actions$.pipe(
    ofType(GET_SINGLE_WEBHOOK),
    map((action: GetSingleWebhookAction) => action.payload),
    switchMap((id: number) =>
      this.webhooksService.getSingle(id).pipe(map(response => new GetSingleWebhookSuccessAction(response)))
    )
  );

  @Effect()
  saveWebhook = this.actions$.pipe(
    ofType(SAVE_WEBHOOK),
    map((action: SaveWebhookAction) => action.payload),
    switchMap((webhook: WebhookSubscription) => {
      return this.webhooksService.checkIfLimitsExceeded().switchMap(({ limitsExceeded }) => {
        if (limitsExceeded) {
          this.ng1ModalService.lazyModalService.showUpgradeAccountModal();
          return EMPTY;
        }
        return this.webhooksService.saveWebhook(webhook).pipe(map(() => new SaveWebhookSuccessAction()));
      });
    })
  );

  @Effect()
  updateWebhook = this.actions$.pipe(
    ofType(UPDATE_WEBHOOK),
    map((action: UpdateWebhookAction) => action.payload),
    switchMap(({ id, webhook }) =>
      this.webhooksService.update(id, webhook).pipe(map(() => new UpdateWebhookSuccessAction()))
    )
  );

  @Effect()
  deleteWebhook = this.actions$.pipe(
    ofType(DELETE_WEBHOOK),
    map((action: DeleteWebhookAction) => action.payload),
    switchMap(id => this.webhooksService.delete(id).pipe(map(() => new DeleteWebhookSuccessAction(id))))
  );

  @Effect()
  changeWebhookActivity = this.actions$.pipe(
    ofType(CHANGE_WEBHOOK_ACTIVITY),
    map((action: ChangeWebhookActivityAction) => action.payload),
    switchMap(({ id, isActive }) => {
      const action = isActive ? this.webhooksService.activate(id) : this.webhooksService.deactivate(id);
      return action.pipe(map(() => new ChangeWebhookActivitySuccessAction()));
    })
  );

  @Effect({ dispatch: false })
  redirectToList = this.actions$.pipe(
    ofType(SAVE_WEBHOOK_SUCCESS, UPDATE_WEBHOOK_SUCCESS),
    tap(() => {
      this.router.navigateByUrl('/webhooks/list');
    })
  );
}
